'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, TrendingUp, BarChart2, Briefcase, Wallet, ChevronLeft, ChevronRight, Settings, Bell, LogOut, BookOpen, ClipboardList, Link2 } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const navItems = [
  { label: 'Dashboard', href: '/market-dashboard', icon: LayoutDashboard, badge: null },
  { label: 'Trade', href: '/trading-screen', icon: TrendingUp, badge: null },
  { label: 'Portfolio', href: '/portfolio-holdings', icon: Briefcase, badge: '3' },
  { label: 'Trade Journal', href: '/trade-journal', icon: BookOpen, badge: null },
  { label: 'EOD Settlement', href: '/eod-settlement', icon: ClipboardList, badge: null },
  { label: 'Alerts', href: '/alerts', icon: Bell, badge: null },
  { label: 'Wallet', href: '/wallet', icon: Wallet, badge: null },
  { label: 'Brokers', href: '/broker-connections', icon: Link2, badge: null },
  { label: 'Charts', href: '/trading-screen', icon: BarChart2, badge: null },
];

interface SidebarProps {
  activePath?: string;
}

export default function Sidebar({ activePath }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const { user, signOut } = useAuth();
  const router = useRouter();

  const handleLogout = async () => {
    try {
      await signOut();
      router.push('/sign-up-login-screen');
      router.refresh();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';
  const displayEmail = user?.email || '';
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <aside
      className={`relative flex flex-col bg-secondary border-r border-border transition-all duration-300 ease-in-out flex-shrink-0 ${
        collapsed ? 'w-16' : 'w-56'
      } hidden md:flex`}
    >
      {/* Logo */}
      <div className={`flex items-center gap-2 px-3 py-4 border-b border-border ${collapsed ? 'justify-center' : ''}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-semibold text-sm text-foreground tracking-tight">
            MyTrading
          </span>
        )}
      </div>

      {/* Nav Items */}
      <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto scrollbar-thin">
        <div className={`text-2xs font-semibold uppercase tracking-widest text-muted-foreground px-2 mb-2 ${collapsed ? 'hidden' : ''}`}>
          Markets
        </div>
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = activePath === item.href || (item.href === '/market-dashboard' && activePath === '/');
          return (
            <Link
              key={`nav-${item.label}`}
              href={item.href}
              className={`flex items-center gap-3 px-2 py-2 rounded-md text-sm font-medium transition-all duration-150 group relative ${
                isActive
                  ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              } ${collapsed ? 'justify-center' : ''}`}
              title={collapsed ? item.label : undefined}
            >
              <Icon size={18} className="flex-shrink-0" />
              {!collapsed && <span className="truncate">{item.label}</span>}
              {!collapsed && item.badge && (
                <span className="ml-auto bg-primary/20 text-primary text-2xs font-semibold px-1.5 py-0.5 rounded-full">
                  {item.badge}
                </span>
              )}
              {collapsed && item.badge && (
                <span className="absolute top-0.5 right-0.5 w-2 h-2 bg-primary rounded-full" />
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="px-2 py-3 border-t border-border space-y-1">
        <Link
          href="#"
          className={`flex items-center gap-3 px-2 py-2 rounded-md text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150 ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? 'Notifications' : undefined}
        >
          <Bell size={18} />
          {!collapsed && <span>Alerts</span>}
          {!collapsed && <span className="ml-auto bg-warning/20 text-warning text-2xs font-semibold px-1.5 py-0.5 rounded-full">5</span>}
        </Link>
        <Link
          href="#"
          className={`flex items-center gap-3 px-2 py-2 rounded-md text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150 ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? 'Settings' : undefined}
        >
          <Settings size={18} />
          {!collapsed && <span>Settings</span>}
        </Link>
        <button
          onClick={handleLogout}
          className={`w-full flex items-center gap-3 px-2 py-2 rounded-md text-sm text-muted-foreground hover:bg-negative-subtle hover:text-negative transition-all duration-150 ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? 'Logout' : undefined}
        >
          <LogOut size={18} />
          {!collapsed && <span>Logout</span>}
        </button>

        {/* User chip */}
        {!collapsed && (
          <div className="flex items-center gap-2 px-2 py-2 mt-1">
            <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-primary">{initials}</span>
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-foreground truncate">{displayName}</p>
              <p className="text-2xs text-muted-foreground truncate">{displayEmail}</p>
            </div>
          </div>
        )}
      </div>

      {/* Collapse Toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-card border border-border rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary transition-all duration-150 z-10"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}